import { api } from 'sharedKernel/api/config';
import { cookies } from 'sharedKernel/cookies/behavior';
import { API_BASE_URL } from 'sharedKernel/constants';

export enum TrackStatus {
  Draft = 1,
  Publish = 2
}

export interface LessonPermissions {
  allowFileUploads?: boolean;
  allowQuestions?: boolean;
}

export interface TrackCreateRequest {
  title: string;
  description?: string;
  imageUrl?: string;
  status: 1 | 2;
  lessons: TrackLessonRequest[];
}

export interface TrackUpdateRequest {
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  status: 1 | 2;
  lessons: TrackLessonUpdateRequest[];
}

export interface TrackLessonRequest {
  title: string;
  description?: string;
  videoId?: string | null;
  minutes?: number;
  sequence?: number;
  permissions?: LessonPermissions;
}

export interface TrackLessonUpdateRequest {
  id?: string;
  title: string;
  description?: string;
  videoId?: string | null;
  videoUrl?: string;
  videoProvider: string,  
  minutes?: number;
  sequence?: number;
  permissions?: LessonPermissions;
  originalVideoProvider: string;
}

export interface TrackResponse {
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  status: 'Draft' | 'Publish';
  createdAt: string;
  numberOfLessons: number;
  totalOfMinutes: number;
}

export interface TracksListResponse {
  rows: TrackResponse[];
  totalPages: number;
  totalRows: number;
  page: number;
  limit: number;
}

export interface TrackSubscription {
  id: string;
  companyId: string;
  trackId: string;
  subscribedAt: string;
}

export interface TrackProgress {
  id: string;
  subscriptionId: string;
  currentLessonId: string;
  progress: number;
  status: 'InProgress' | 'Concluded';
  updatedAt: string;
}

export interface LessonProgress {
  id: string;
  subscriptionId: string;
  lessonId: string;
  status: 'NotStarted' | 'InProgress' | 'Completed';
  progress: number;
  startedAt?: string;
  finishedAt?: string;
}

export interface TrackSubscriptionResponse {
  subscription: TrackSubscription;
  trackProgress: TrackProgress;
  lessonProgresses: LessonProgress[];
}

export interface LessonFile {
  id: string;
  lessonId: string;
  name: string;
  url: string;
  mimetype: string;
  size: number;
}

export interface TrackDetailResponse extends TrackResponse {
  lessons: Array<{
    id: string;
    trackId: string;
    title: string;
    description?: string;
    videoId?: string | null;
    embeddedVideoUrl: string;
    videoProvider: string,
    minutes: number;
    sequence: number;
    createdAt: string;
    updatedAt: string;
    permissions?: LessonPermissions;
    files?: LessonFile[];
    userProgressStatus?: 'NotStarted' | 'InProgress' | 'Completed';
    userProgress?: number;
    userStartedAt?: string;
    userFinishedAt?: string;
  }>;
  subscriptions?: Array<{
    id: string;
    companyId: string;
    trackId: string;
    subscribedAt: string;
  }>;
  userProgress?: number;
  userCompletedLessons?: number;
}

export interface TrackSubscriptionItemResponse {
  id: string;
  companyId: string;
  trackId: string;
  subscribedAt: string;
  progress: number;
  status: 'InProgress' | 'Concluded';
  companyName?: string;
}

export interface TrackCreateResponse {
  id: string;
  lessons?: Array<{
    id: string;
    sequence: number;
  }>;
}

export const trackService = {
  async createTrack(data: TrackCreateRequest): Promise<TrackCreateResponse> {
    const response = await api.post('/v1/tracks', data);
    return response.data;
  },

  async updateTrack(data: TrackUpdateRequest): Promise<void> {
    try {
      if (!data.id || data.id.trim() === "") {
        throw new Error("ID da trilha é obrigatório");
      }
      
      if (!data.title || data.title.trim() === "") {
        throw new Error("Título da trilha é obrigatório");
      }
      
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!guidRegex.test(data.id)) {
        throw new Error("ID da trilha deve ser um GUID válido");
      }
      
      const isValidGuid = (id: string): boolean => {
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return guidRegex.test(id);
      };

      const getVideoUrl = (lesson: TrackLessonUpdateRequest) =>
      {
        //if (lesson.videoProvider !== lesson.


      }
      
      const requestData = {
        id: data.id, 
        title: data.title.trim(), 
        description: data.description?.trim() || "", 
        imageUrl: data.imageUrl || "", 
        status: Number(data.status),
        lessons: data.lessons.map((lesson, index) => {
          let lessonId = "00000000-0000-0000-0000-000000000000";
          
          if (lesson.id && lesson.id !== "" && isValidGuid(String(lesson.id))) {
            lessonId = String(lesson.id);
          } else if (lesson.id && lesson.id !== "") {
            lessonId = "00000000-0000-0000-0000-000000000000";
          }

          return {
            id: lessonId,
            title: lesson.title.trim(), 
            description: lesson.description?.trim() || "",
            videoId: lesson.videoId || null, 
            videoUrl: lesson.videoUrl,
            videoProvider: lesson.videoProvider,
            minutes: Number(lesson.minutes || 0), 
            sequence: Number(lesson.sequence || index + 1),
            permissions: lesson.permissions || {
              allowFileUploads: false,
              allowQuestions: false
            }
          };
        })
      };
      
      const response = await api.put('/v1/tracks', requestData);
      
      if (response.status === 202) {
        return;
      }
      
      return;
    } catch (error: any) {
      
      throw error;
    }
  },

  async getTrack(trackId: string): Promise<TrackDetailResponse> {
    const response = await api.get(`/v1/tracks/${trackId}`);
    return response.data;
  },

  async deleteTrack(trackId: string): Promise<void> {
    const token = localStorage.getItem('jwt_token') || sessionStorage.getItem('jwt') || cookies.get('_cred')?.data?.token;
    
    if (token) {
      try {
        const response = await fetch(`${API_BASE_URL}v1/tracks/${trackId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          return;
        } else {
          throw new Error(`Erro ao excluir trilha: ${response.status}`);
        }
      } catch (error) {
        throw error;
      }
    }
    
    await api.delete(`/v1/tracks/${trackId}`);
  },

  async getTracks(page: number = 0, limit: number = 20, sort: string = 'CreatedAt desc', match?: string, filterBy?: any[]): Promise<TracksListResponse> {
    const token = localStorage.getItem('jwt_token') || sessionStorage.getItem('jwt') || cookies.get('_cred')?.data?.token;
    
    const params: any = { 
      page: page.toString(), 
      limit: limit.toString(), 
      sort
    };
    
    if (match) {
      params.match = match;
    }
    
    if (filterBy && filterBy.length > 0) {
      filterBy.forEach((filter, index) => {
        Object.keys(filter).forEach(key => {
          params[`filterBy[${index}][${key}]`] = filter[key];
        });
      });
    }
    
    if (token) {
      try {
        const queryString = new URLSearchParams(params).toString();
        const response = await fetch(`${API_BASE_URL}v1/tracks?${queryString}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          return data;
        }
      } catch (error) {
      }
    }
    
    try {
      const response = await api.get('/v1/tracks', { params });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },


  async getMyLearning(): Promise<TracksListResponse> {
    const response = await api.get('/v1/tracks/mylearning');
    return response.data;
  },

  async togglePublishTrack(trackId: string): Promise<TrackResponse> {
    const response = await api.patch(`/v1/tracks/publish/${trackId}`);
    return response.data;
  },

  async getAvailableTracks(filters: {
    page?: number;
    limit?: number;
    sort?: string;
    match?: string;
    filterBy?: any[];
  } = {}): Promise<TracksListResponse> {
    const params: any = {
      page: (filters.page || 0).toString(),
      limit: (filters.limit || 20).toString(),
      sort: filters.sort || 'CreatedAt desc'
    };
    
    if (filters.match) {
      params.match = filters.match;
    }
    
    if (filters.filterBy && filters.filterBy.length > 0) {
      filters.filterBy.forEach((filter, index) => {
        Object.keys(filter).forEach(key => {
          params[`filterBy[${index}][${key}]`] = filter[key];
        });
      });
    }

    const response = await api.get('/v1/tracks/availible', { params });
    return response.data;
  },

  async startTrack(trackId: string): Promise<TrackSubscriptionResponse> {
    try {
      const response = await api.post(`/v1/tracks/start/${trackId}`);
      
      if (response.status === 201) {
        return {
          success: true,
          subscription: response.data,
          message: 'Trilha iniciada com sucesso!'
        } as any;
      }
      
      return response.data;
    } catch (error: any) {
      
      const { message, code, errors } = error?.response?.data || {};
      const statusCode = error?.response?.status;
      
      switch (statusCode) {
        case 400:
          throw new Error('Dados inválidos. Verifique os dados enviados.');
          
        case 404:
          throw new Error('Trilha não encontrada. A trilha solicitada não existe ou foi removida.');
          
        case 409:
          throw new Error('Você já está inscrito nesta trilha.');
          
        case 500:
          throw new Error('Ocorreu um erro interno. O servidor está temporariamente indisponível. Tente novamente mais tarde.');
          
        default:
          throw new Error(message || 'Ocorreu um erro inesperado ao iniciar a trilha.');
      }
    }
  },

  async getMyLearningTracks(filters: {
    page?: number;
    limit?: number;
    sort?: string;
    match?: string;
    filterBy?: any[];
  } = {}): Promise<TracksListResponse> {
    const params: any = {
      page: (filters.page || 0).toString(),
      limit: (filters.limit || 20).toString(),
      sort: filters.sort || 'CreatedAt desc'
    };
    
    if (filters.match) {
      params.match = filters.match;
    }
    
    if (filters.filterBy && filters.filterBy.length > 0) {
      filters.filterBy.forEach((filter, index) => {
        Object.keys(filter).forEach(key => {
          params[`filterBy[${index}][${key}]`] = filter[key];
        });
      });
    }

    const response = await api.get('/v1/tracks/mylearning', { params });
    return response.data;
  },

  async getTrackDetails(trackId: string): Promise<TrackDetailResponse> {
    const response = await api.get(`/v1/tracks/${trackId}`);
    return response.data;
  },

  async getTrackSubscriptions(trackId: string, filters: {
    page?: number;
    limit?: number;
    sort?: string;
    search?: string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    filterBy?: any[];
  } = {}): Promise<TrackSubscriptionsResponse> {
    const params: any = {
      page: (filters.page || 0).toString(),
      limit: (filters.limit || 20).toString(),
      sort: filters.sort || 'CreatedAt desc'
    };
    
    if (filters.search) {
      params.search = filters.search;
    }
    if (filters.sortBy) {
      params.sortBy = filters.sortBy;
    }
    if (filters.sortDirection) {
      params.sortDirection = filters.sortDirection;
    }
    
    if (filters.filterBy && filters.filterBy.length > 0) {
      filters.filterBy.forEach((filter, index) => {
        Object.keys(filter).forEach(key => {
          params[`filterBy[${index}][${key}]`] = filter[key];
        });
      });
    }

    const endpoint = `/v1/tracks/${trackId}/subscriptions`;

    try {
      const response = await api.get(endpoint, { params });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  async uploadLessonVideo(lessonId: string, videoFile: File, minutes: number): Promise<void> {
    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('minute', minutes.toString());

    const response = await api.post(`/v1/lesson/upload-video/${lessonId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async updateLessonProgress(
    lessonId: string, 
    subscriptionId: string, 
    status: "NotStarted" | "InProgress" | "Completed"
  ): Promise<any> {
    const statusMap = {
      "NotStarted": 0,
      "InProgress": 1,
      "Completed": 2
    };
    
    const numericStatus = statusMap[status];
    
    
    const response = await api.post(
      `/v1/tracks/track-progress/${lessonId}/${subscriptionId}`,
      numericStatus
    );
    
    return response.data;
  }
};

export interface LessonResponse {
  id: string;
  trackId: string;
  title: string;
  description: string;
  videoId: string | null;
  minutes: number;
  sequence: number;
  createdAt: string;
  updatedAt: string;
  permissions: PermissionsResponse;
  files: LessonFileResponse[];
}

export interface PermissionsResponse {
  allowQuestions?: boolean;
  allowFileUploads?: boolean;
}

export interface LessonFileResponse {
  id: string;
  lessonId: string;
  name: string;
  url: string;
  mimetype: string;
  size: number;
}

export interface TrackSubscriptionsResponse {
  rows: TrackSubscriptionItem[];
  totalPages: number;
  totalRows: number;
  page: number;
  limit: number;
}

export interface TrackSubscriptionItem {
  subscriptionId: string;
  appUserId: string;
  appUserName: string;
  currentLessonLabel: string;
  progress: number;
  status: 'InProgress' | 'Completed' | 'NotStarted' | string;
}

export interface TrackStartResponse {
  id: string;
  trackId: string;
  companyId: string;
  subscribedAt: string;
}

export interface MyLearningResponse {
  tracks: MyLearningTrack[];
}

export interface MyLearningTrack {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  status: 'Publish' | 'Draft';
  createdAt: string;
  updatedAt?: string;
  numberOfLessons: number;
  totalOfMinutes: number;
  progress?: number;
  learningStatus?: 'InProgress' | 'Completed' | 'NotStarted';
  currentLesson?: {
    id: string;
    title: string;
    sequence: number;
  };
  totalLessons?: number;
  completedLessons?: number;
}

export const uploadTrackImage = async (trackId: string, file: File): Promise<string> => {
  try {
    const base64 = await fileToBase64(file);
    
    const base64FileName = btoa(file.name);
    
    const uploadData = [{
      content: base64,
      mimetype: file.type,
      name: base64FileName, 
      type: "thumbnail",
      trackId: trackId,
      lessonId: "00000000-0000-0000-0000-000000000000" 
    }];


    const response = await api.post('/v1/tracks/AddFiles', uploadData);
    
    let imageUrl = '';
    if (response.data) {
      if (Array.isArray(response.data) && response.data[0]) {
        imageUrl = response.data[0].url || response.data[0].imageUrl || '';
      } else if (response.data.url) {
        imageUrl = response.data.url;
      } else if (response.data.imageUrl) {
        imageUrl = response.data.imageUrl;
      }
    }
    
    return imageUrl;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || 'Erro no upload da imagem');
  }
};

export const getVideoStatus = async (videoId: string): Promise<any> => {
  try {
    const response = await api.get(`/v1/lesson/video/status/${videoId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || 'Erro ao buscar status do vídeo');
  }
};

export const uploadLessonFiles = async (trackId: string, lessonId: string, files: File[]): Promise<any[]> => {
  try {
    const uploadData = await Promise.all(
      files.map(async (file) => {
        const base64 = await fileToBase64(file);
        const base64FileName = btoa(file.name);
        
        return {
          content: base64,
          mimetype: file.type,
          name: base64FileName,
          type: "attachment",
          trackId: trackId,
          lessonId: lessonId
        };
      })
    );

    const response = await api.post('/v1/tracks/AddFiles', uploadData);
    
    if (response.data) {
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data.files) {
        return response.data.files;
      }
    }
    
    return [];
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || 'Erro no upload dos arquivos da aula');
  }
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      const base64Data = base64.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = error => reject(error);
  });
};
